import React, { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useLocation } from 'react-router-dom'
import { LoadingSpinner } from '../components/common/LoadingSpinner'
import { RichTextContent } from '../components/common/RichTextContent'
import { TaskViewDialog } from '../components/features/TaskViewDialog'
import { useAuth } from '../context/AuthContext'
import { TASK_PRIORITIES, TASK_PRIORITY_COLORS, TASK_PRIORITY_LABELS, TASK_STATUS_COLORS, TASK_STATUS_LABELS, TASK_TYPE_LABELS, TaskStatus } from '../lib/config/project-config'
import { canProfileSeeStatus } from '../lib/config/status-config'
import { queries } from '../lib/supabase/queries'
import { exportTasksToExcel } from '../utils/exportToExcel'
import { Task } from '../lib/types/database.types'
import { formatDate, formatDateTime } from '../utils/formatters'
import { formatFileSize } from '../utils/fileSize'
import { getTaskCurrentStatusDueDate, isTaskStatusOverdue } from '../utils/statusDates'

const EyeIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className={className} aria-hidden="true">
    <path d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6Z" strokeLinecap="round" strokeLinejoin="round" />
    <circle cx="12" cy="12" r="2.5" />
  </svg>
)

type LifecycleBranch = {
  label: string
  statuses: TaskStatus[]
  priorityBreakdown?: boolean
}

const LIFECYCLE_BRANCHES: LifecycleBranch[] = [
  { label: 'Approval', statuses: ['pending_quote', 'pending_finance_approval'] },
  { label: 'In Development', statuses: ['in_dev_progress'], priorityBreakdown: true },
  { label: 'Testing', statuses: ['in_dev_testing', 'in_uat_testing'] },
  { label: 'Release', statuses: ['ready_for_uat', 'ready_for_prod'] },
]

const SUPPORTING_LIFECYCLE_BRANCHES: LifecycleBranch[] = [
  { label: 'On Hold', statuses: ['on_hold'] },
  { label: 'Completed', statuses: ['completed'] },
]

export const DashboardPage: React.FC = () => {
  const location = useLocation()
  const { profile } = useAuth()
  const [isTaskListModalOpen, setIsTaskListModalOpen] = useState(false)
  const [selectedTaskListTitle, setSelectedTaskListTitle] = useState('')
  const [selectedTaskIds, setSelectedTaskIds] = useState<string[]>([])
  const [viewingTask, setViewingTask] = useState<Task | null>(null)

  const taskNotesQuery = useQuery({
    queryKey: ['task-notes', viewingTask?.id],
    queryFn: () => queries.getTaskNotes(viewingTask!.id),
    enabled: Boolean(viewingTask),
  })
  const taskAttachmentsQuery = useQuery({
    queryKey: ['task-attachments', viewingTask?.id],
    queryFn: () => queries.getTaskAttachments(viewingTask!.id),
    enabled: Boolean(viewingTask),
  })
  const taskTimeEntriesQuery = useQuery({
    queryKey: ['task-time-entries', viewingTask?.id],
    queryFn: () => queries.getTaskTimeEntries(viewingTask!.id),
    enabled: Boolean(viewingTask),
  })

  const statusQuery = useQuery({
    queryKey: ['dashboard', 'status'],
    queryFn: queries.getTasksByStatus,
  })

  const tasksQuery = useQuery({
    queryKey: ['tasks', 'dashboard-review'],
    queryFn: () => queries.getTasks(false),
  })

  const profilesQuery = useQuery({
    queryKey: ['profiles', 'all'],
    queryFn: queries.getAllProfiles,
  })

  const isLoading = statusQuery.isLoading || tasksQuery.isLoading || profilesQuery.isLoading
  const error = statusQuery.error || tasksQuery.error || profilesQuery.error

  const profileMap = useMemo(
    () => new Map((profilesQuery.data || []).map((item) => [item.id, item.full_name || item.email || 'Unknown user'])),
    [profilesQuery.data]
  )
  const visibleTasks = useMemo(
    () => (tasksQuery.data || []).filter((task) => canProfileSeeStatus(profile, task.status)),
    [tasksQuery.data, profile]
  )
  const readyForNextReleaseTasks = useMemo(
    () => visibleTasks.filter((task) => task.status === 'ready_for_prod'),
    [visibleTasks]
  )
  const selectedTasks = useMemo(() => {
    const taskIdSet = new Set(selectedTaskIds)
    return visibleTasks.filter((task) => taskIdSet.has(task.id))
  }, [selectedTaskIds, visibleTasks])
  const selectedTaskAssignees = useMemo(
    () =>
      Array.from(
        new Set(
          selectedTasks.map((task) =>
            task.assigned_to ? profileMap.get(task.assigned_to) || 'Unknown user' : 'Unassigned'
          )
        )
      ).sort((left, right) => left.localeCompare(right)),
    [selectedTasks, profileMap]
  )


  const statusCounts = useMemo(
    () => new Map((statusQuery.data || []).map((item) => [item.status, item.count])),
    [statusQuery.data]
  )
  const openTaskListModal = (title: string, taskIds: string[]) => {
    setSelectedTaskListTitle(title)
    setSelectedTaskIds(taskIds)
    setIsTaskListModalOpen(true)
  }

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    if (params.get('taskList') !== 'ready_for_next_release') {
      return
    }

    if (tasksQuery.isLoading) {
      return
    }

    openTaskListModal('Ready for Next Release', readyForNextReleaseTasks.map((task) => task.id))

    params.delete('taskList')
    const nextQuery = params.toString()
    const nextUrl = `${window.location.pathname}${nextQuery ? `?${nextQuery}` : ''}`
    window.history.replaceState({}, document.title, nextUrl)
  }, [location.search, readyForNextReleaseTasks, tasksQuery.isLoading])

  const handleExportSelectedTasks = () => {
    if (selectedTasks.length === 0) {
      return
    }

    const today = new Date().toISOString().slice(0, 10)
    const normalizedTitle = selectedTaskListTitle
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
    const baseName = normalizedTitle || 'dashboard-tasks'
    exportTasksToExcel(selectedTasks, profileMap, `${baseName}-${today}.xlsx`)
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-1.5 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-xs text-gray-600">Follow every task from intake through release.</p>
        </div>
      </div>

      {isLoading && (
        <div className="flex min-h-[200px] items-center justify-center border-y border-stone-300">
            <LoadingSpinner text="Loading dashboard data..." />
        </div>
      )}

      {error && !isLoading && (
        <div className="border-y border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {error instanceof Error ? error.message : 'Failed to load dashboard data.'}
        </div>
      )}

      {!isLoading && !error && (
        <div className="border-y border-stone-300 py-6">
          <div className="overflow-x-auto pb-3">
            <ol role="tree" aria-label="Task lifecycle" className="flex min-w-[980px] items-stretch">
              {LIFECYCLE_BRANCHES.map((branch, branchIndex) => {
                const visibleStatuses = branch.statuses.filter((status) => canProfileSeeStatus(profile, status))
                const stageTaskCount = visibleStatuses.reduce((total, status) => total + (statusCounts.get(status) || 0), 0)

                return (
                  <React.Fragment key={branch.label}>
                    <li role="treeitem" aria-expanded="true" className="flex min-w-0 flex-1 flex-col">
                      <div className="mb-4 flex items-center gap-2">
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-700 text-xs font-bold text-stone-100">
                          {branchIndex + 1}
                        </span>
                        <div>
                          <h3 className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-700">{branch.label}</h3>
                          <p className="mt-1 text-xs text-slate-500">{stageTaskCount} task{stageTaskCount === 1 ? '' : 's'}</p>
                        </div>
                      </div>
                      <ul role="group" className="flex flex-1 flex-col gap-2 border-l-2 border-slate-300 pl-3 pr-3">
                        {branch.priorityBreakdown ? TASK_PRIORITIES.slice().reverse().map((priority) => {
                          const priorityTasks = visibleTasks.filter((task) => task.status === 'in_dev_progress' && task.priority === priority)
                          const taskIds = priorityTasks.map((task) => task.id)
                          const count = priorityTasks.length
                          const overdueCount = priorityTasks.filter(isTaskStatusOverdue).length

                          return (
                            <li key={priority} className="relative">
                              <button
                                type="button"
                                onClick={() => openTaskListModal(`${TASK_STATUS_LABELS.in_dev_progress} - ${TASK_PRIORITY_LABELS[priority]}`, taskIds)}
                                className="group flex min-h-16 w-full items-center justify-between gap-3 rounded-lg border border-stone-300 bg-stone-100 px-3 py-2 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-slate-500 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2"
                              >
                                <span className="flex min-w-0 items-center gap-2">
                                  <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: TASK_PRIORITY_COLORS[priority] }} aria-hidden="true" />
                                  <span className="text-xs font-medium leading-tight text-gray-800 group-hover:text-slate-950">{TASK_PRIORITY_LABELS[priority]} priority</span>
                                </span>
                                <span className="flex items-center gap-2 whitespace-nowrap">
                                  {overdueCount > 0 && <span className="text-xs font-semibold text-red-600">(Overdue: {overdueCount})</span>}
                                  <span className="text-lg font-semibold text-slate-900">{count}</span>
                                </span>
                              </button>
                            </li>
                          )
                        }) : visibleStatuses.map((status) => {
                          const statusTasks = visibleTasks.filter((task) => task.status === status)
                          const taskIds = statusTasks.map((task) => task.id)
                          const count = statusCounts.get(status) || 0
                          const overdueCount = statusTasks.filter(isTaskStatusOverdue).length
                          const color = TASK_STATUS_COLORS[status]

                          return (
                            <li key={status} className="relative">
                              <button
                                type="button"
                                onClick={() => openTaskListModal(TASK_STATUS_LABELS[status], taskIds)}
                                className="group flex min-h-16 w-full items-center justify-between gap-3 rounded-lg border border-stone-300 bg-stone-100 px-3 py-2 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-slate-500 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2"
                              >
                                <span className="flex min-w-0 items-center gap-2">
                                  <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: color }} aria-hidden="true" />
                                  <span className="text-xs font-medium leading-tight text-gray-800 group-hover:text-slate-950">{TASK_STATUS_LABELS[status]}</span>
                                </span>
                                <span className="flex items-center gap-2 whitespace-nowrap">
                                  {overdueCount > 0 && <span className="text-xs font-semibold text-red-600">(Overdue: {overdueCount})</span>}
                                  <span className="text-lg font-semibold text-slate-900">{count}</span>
                                </span>
                              </button>
                            </li>
                          )
                        })}
                      </ul>
                    </li>
                    {branchIndex < LIFECYCLE_BRANCHES.length - 1 && (
                      <div className="flex w-8 shrink-0 items-start pt-4" aria-hidden="true">
                        <span className="relative mt-4 h-0.5 w-full bg-slate-400 after:absolute after:-right-0.5 after:-top-1 after:border-y-4 after:border-l-4 after:border-y-transparent after:border-l-slate-400" />
                      </div>
                    )}
                  </React.Fragment>
                )
              })}
            </ol>
          </div>

          <div className="mt-6 border-t border-stone-300 pt-5">
            <div className="mb-4">
              <h3 className="text-sm font-semibold uppercase tracking-[0.12em] text-slate-700">Other statuses</h3>
            </div>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {SUPPORTING_LIFECYCLE_BRANCHES.map((branch) => {
                const status = branch.statuses[0]
                const statusTasks = visibleTasks.filter((task) => task.status === status)
                const taskIds = statusTasks.map((task) => task.id)
                const overdueCount = statusTasks.filter(isTaskStatusOverdue).length

                return (
                  <button
                    key={status}
                    type="button"
                    onClick={() => openTaskListModal(TASK_STATUS_LABELS[status], taskIds)}
                    className="group flex min-h-16 items-center justify-between gap-3 rounded-lg border border-stone-300 bg-stone-100 px-4 py-2 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-slate-500 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2"
                  >
                    <span className="flex items-center gap-2">
                      <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: TASK_STATUS_COLORS[status] }} aria-hidden="true" />
                      <span className="text-sm font-medium text-gray-800 group-hover:text-slate-950">{branch.label}</span>
                    </span>
                    <span className="flex items-center gap-2 whitespace-nowrap">
                      {overdueCount > 0 && <span className="text-xs font-semibold text-red-600">(Overdue: {overdueCount})</span>}
                      <span className="text-lg font-semibold text-slate-900">{statusCounts.get(status) || 0}</span>
                    </span>
                  </button>
                )
              })}
            </div>
          </div>
        </div>
      )}

      {isTaskListModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 p-4">
          <div className="max-h-[85vh] w-full max-w-5xl overflow-hidden rounded-xl border border-stone-300 bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">{selectedTaskListTitle}</h2>
                <p className="text-sm text-gray-500">{selectedTasks.length} item{selectedTasks.length === 1 ? '' : 's'}</p>
                {selectedTaskListTitle === 'Ready for Next Release' && (
                  <p className="mt-1 text-xs text-gray-500">
                    Assigned to: {selectedTaskAssignees.length > 0 ? selectedTaskAssignees.join(', ') : 'Unassigned'}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  className="rounded-md border border-stone-300 bg-stone-100 px-3 py-1.5 text-sm font-medium text-slate-700 transition hover:bg-stone-200 disabled:cursor-not-allowed disabled:opacity-50"
                  onClick={handleExportSelectedTasks}
                  disabled={selectedTasks.length === 0}
                >
                  Export to Excel
                </button>
                <button
                  type="button"
                  className="text-gray-500 transition hover:text-gray-700"
                  onClick={() => setIsTaskListModalOpen(false)}
                >
                  Close
                </button>
              </div>
            </div>

            <div className="max-h-[calc(85vh-84px)] overflow-auto p-6">
              {selectedTasks.length === 0 ? (
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
                  No tasks found for this section.
                </div>
              ) : (
                <div className="overflow-x-auto rounded-lg border border-gray-200">
                  <table className="w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50">
                      <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
                        <th className="px-4 py-3">Added By</th>
                        <th className="px-4 py-3">Assigned To</th>
                        <th className="px-4 py-3">Title</th>
                        <th className="px-4 py-3">Related App</th>
                        <th className="px-4 py-3">Type</th>
                        <th className="px-4 py-3">Requested By</th>
                        <th className="px-4 py-3">Due Date</th>
                        <th className="px-4 py-3 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {selectedTasks.map((task) => (
                        <tr key={task.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-gray-700">{profileMap.get(task.created_by) || 'Unknown user'}</td>
                          <td className="px-4 py-3 text-gray-700">
                            {task.assigned_to ? profileMap.get(task.assigned_to) || 'Unknown user' : 'Unassigned'}
                          </td>
                          <td className="px-4 py-3 font-medium text-gray-900">{task.title}</td>
                          <td className="px-4 py-3 text-gray-700">{task.related_app || 'None'}</td>
                          <td className="px-4 py-3 text-gray-700">{TASK_TYPE_LABELS[task.type as keyof typeof TASK_TYPE_LABELS] || task.type}</td>
                          <td className="px-4 py-3 text-gray-700">{task.requested_by || 'Not set'}</td>
                          <td className="px-4 py-3 text-gray-700">
                            {getTaskCurrentStatusDueDate(task) ? formatDate(getTaskCurrentStatusDueDate(task)!) : 'Not set'}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <button
                              type="button"
                              title={`View details for ${task.title}`}
                              aria-label={`View details for ${task.title}`}
                              className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-stone-300 bg-stone-100 text-slate-600 transition hover:border-slate-500 hover:bg-stone-200 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2"
                              onClick={() => {
                                setIsTaskListModalOpen(false)
                                setViewingTask(task)
                              }}
                            >
                              <EyeIcon className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {viewingTask && (
        <TaskViewDialog
          task={viewingTask}
          assigneeLabel={viewingTask.assigned_to ? profileMap.get(viewingTask.assigned_to) || 'Unknown user' : 'Unassigned'}
          createdByLabel={profileMap.get(viewingTask.created_by) || 'Unknown user'}
          parentTaskTitle={viewingTask.parent_task_id
            ? (tasksQuery.data || []).find((task) => task.id === viewingTask.parent_task_id)?.title || 'Parent task'
            : undefined}
          fullScreen
          onClose={() => setViewingTask(null)}
        >
          <section className="rounded-xl border border-stone-300 bg-stone-50 p-5 xl:col-span-2">
            <div className="mb-4">
              <h3 className="text-base font-semibold text-gray-900">Sub-tasks</h3>
              <p className="text-sm text-gray-500">Tasks grouped under this parent</p>
            </div>
            {(tasksQuery.data || []).filter((task) => task.parent_task_id === viewingTask.id).length === 0 ? (
              <p className="text-sm text-gray-500">No sub-tasks.</p>
            ) : (
              <div className="space-y-2">
                {(tasksQuery.data || [])
                  .filter((task) => task.parent_task_id === viewingTask.id)
                  .map((subTask) => (
                    <div key={subTask.id} className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white/80 p-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-gray-900">{subTask.title}</p>
                        <p className="text-xs text-gray-500">{subTask.status}</p>
                      </div>
                      <span className="text-xs font-medium text-gray-600">{subTask.assigned_to ? profileMap.get(subTask.assigned_to) || 'Unknown user' : 'Unassigned'}</span>
                    </div>
                  ))}
              </div>
            )}
          </section>

          <section className="rounded-xl border border-stone-300 bg-stone-50 p-5 xl:col-span-2">
            <div className="mb-4">
              <h3 className="text-base font-semibold text-gray-900">Attachments</h3>
              <p className="text-sm text-gray-500">Files attached to this task</p>
            </div>
            {taskAttachmentsQuery.isLoading ? (
              <LoadingSpinner text="Loading attachments..." />
            ) : taskAttachmentsQuery.data?.length ? (
              <div className="space-y-3">
                {taskAttachmentsQuery.data.map((attachment) => (
                  <div key={attachment.id} className="rounded-lg border border-gray-200 bg-white/80 p-3">
                    <p className="truncate text-sm font-medium text-gray-900">{attachment.file_name}</p>
                    <p className="text-xs text-gray-500">
                      Added by {attachment.uploader_name} on {formatDateTime(attachment.created_at)}
                    </p>
                    <p className="text-xs text-gray-500">{attachment.mime_type || 'Unknown type'} | {formatFileSize(attachment.file_size_bytes)}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">No attachments yet.</p>
            )}
          </section>

          <section className="rounded-xl border border-stone-300 bg-stone-50 p-5 xl:col-span-2">
            <div className="mb-4">
              <h3 className="text-base font-semibold text-gray-900">Time Entries</h3>
              <p className="text-sm text-gray-500">Work logged against this task</p>
            </div>
            {taskTimeEntriesQuery.isLoading ? (
              <LoadingSpinner text="Loading time entries..." />
            ) : taskTimeEntriesQuery.data?.length ? (
              <div className="space-y-3">
                {taskTimeEntriesQuery.data.map((entry) => (
                  <div key={entry.id} className="rounded-lg border border-gray-200 bg-white/80 p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{entry.author_name}</p>
                        <p className="text-xs text-gray-500">{formatDateTime(entry.created_at)}</p>
                      </div>
                      <p className="text-right text-sm text-gray-700">
                        {entry.duration_minutes}m | {entry.work_type === 'dev' ? 'Development' : 'Testing'}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">No time entries yet.</p>
            )}
          </section>

          <section className="rounded-xl border border-stone-300 bg-stone-50 p-5 xl:col-span-2">
            <div className="mb-4">
              <h3 className="text-base font-semibold text-gray-900">Notes</h3>
              <p className="text-sm text-gray-500">Notes and activity</p>
            </div>
            {taskNotesQuery.isLoading ? (
              <LoadingSpinner text="Loading notes..." />
            ) : taskNotesQuery.data?.length ? (
              <div className="space-y-4">
                {taskNotesQuery.data.map((note) => (
                  <div key={note.id} className="rounded-lg border border-gray-200 bg-white/80 p-4">
                    <p className="text-sm font-medium text-gray-900">{note.author_name}</p>
                    <p className="mb-3 text-xs text-gray-500">{formatDateTime(note.created_at)}</p>
                    <RichTextContent html={note.content} />
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">No notes yet.</p>
            )}
          </section>
        </TaskViewDialog>
      )}
    </div>
  )
}
